import React, {useCallback} from 'react';
import {Flex, Link, Text, UseToastOptions, useToast} from '@chakra-ui/react';
import {Link as ReactRouterLink} from 'react-router-dom';
import {SavedAccountsContext} from './useSavedAccountsStore';
import {pluralize} from '../../lib/helpers';

const toastId = 'saved-accounts-toast';
const defaultToastOptions: UseToastOptions = {
  duration: 5_000,
  isClosable: true,
  position: 'bottom-left',
  status: 'success',
};

export const useSavedAccountsToast = () => {
  const toast = useToast();

  return useCallback(
    ({
      accountName,
      action,
      numAccounts,
      numUpdatedTags = 0,
    }: SavedAccountsContext) => {
      // Use the account name if only one account was saved
      const updatedAccountsMessage =
        numAccounts === 1 && accountName
          ? accountName
          : `${numAccounts} accounts`;
      const tagsText = pluralize(numUpdatedTags, 'list', 'lists');

      const message = {
        removeTag: `Successfully removed ${updatedAccountsMessage} from ${tagsText}`,
        addTag: `Successfully added ${updatedAccountsMessage} to ${tagsText}`,
        editTag: `Successfully updated ${updatedAccountsMessage}`,
        save: `${updatedAccountsMessage} saved to My Accounts`,
      }[action];

      const description =
        action === 'save' ? (
          <Flex wrap="wrap">
            <Text me={2}>{message}</Text>
            <Link
              as={ReactRouterLink}
              onClick={() => {
                // Close the toast so it doesn't cover the saved accounts status bar
                toast.close(toastId);
              }}
              to="/accounts/saved"
              textDecoration="underline"
            >
              View all saved accounts
            </Link>
          </Flex>
        ) : (
          /* tag */
          <Flex wrap="wrap">
            <Text me={2}>{message}</Text>
          </Flex>
        );

      if (toast.isActive(toastId)) {
        toast.update(toastId, {
          description,
          ...defaultToastOptions,
        });
        return;
      }

      toast({
        id: toastId,
        description,
        ...defaultToastOptions,
      });
    },
    [toast]
  );
};
